import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, CircleDashed, Lock, ShieldCheck } from "lucide-react";
import { gapi, fmtDateTime, fmtEuro, AUDIT_LABEL } from "../api";
import { useGAuth } from "../GAuth";
import { PageHeader, Panel, Badge } from "../components/ui";
import TaskWidget from "../components/TaskWidget";

const KPI = [
  ["nuoviContatti", "Nuovi contatti da gestire", "/gestionale/contatti"],
  ["attivitaOggi", "Attività in scadenza oggi", null],
  ["attivitaScadute", "Attività scadute", "/gestionale/attivita"],
  ["immobiliDisponibili", "Immobili disponibili", "/gestionale/immobili"],
  ["immobiliDaApprovare", "Annunci da approvare", "/gestionale/immobili"],
  ["abbinamentiDaProporre", "Abbinamenti da proporre", "/gestionale/acquirenti"],
  ["contattiSenzaAttivita", "Contatti senza attività da 30 giorni", "/gestionale/contatti"],
  ["incarichiInScadenza", "Incarichi in scadenza (30 gg)", "/gestionale/incarichi"],
  ["documentiMancanti", "Documenti mancanti o richiesti", "/gestionale/documenti"],
  ["trattativeAperte", "Trattative aperte", "/gestionale/trattative"],
  ["provvigioniAttese", "Provvigioni attese (€)", "/gestionale/provvigioni"],
  ["provvigioniIncassate", "Provvigioni incassate (€)", "/gestionale/provvigioni"],
];

function Kpis({ d }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="g-dash-kpis">
      {KPI.filter(([k]) => d[k] !== undefined).map(([k, l, to]) => {
        const body = (<><p className="text-3xl font-display font-semibold" data-testid={`kpi-${k}`}>{k.startsWith("provvigioni") ? fmtEuro(d[k]) : d[k]}</p><p className="text-xs text-anthracite/55 mt-1">{l}</p></>);
        return to ? <Link key={k} to={to} className="rounded-2xl border border-border-subtle bg-white p-4 hover:border-brand transition-colors">{body}</Link> : <div key={k} className="rounded-2xl border border-border-subtle bg-white p-4">{body}</div>;
      })}
    </div>
  );
}

function Richiami({ d }) {
  if (!d.ultimiContatti && !d.richiamiOggi) return null;
  return (
    <Panel title="Nuovi contatti e richiami" testid="g-dash-contacts">
      {(d.richiamiOggi || []).map((c) => <Link key={c.id} to={`/gestionale/contatti/${c.id}`} className="flex justify-between py-1.5 text-sm hover:underline"><span>Richiamare <b>{c.displayName}</b></span><span className="text-anthracite/50">{fmtDateTime(c.prossimoRichiamo)}</span></Link>)}
      {(d.ultimiContatti || []).map((c) => <Link key={c.id} to={`/gestionale/contatti/${c.id}`} className="flex justify-between py-1.5 text-sm hover:underline"><span><Badge tone="blue">nuovo</Badge> {c.displayName}</span><span className="text-anthracite/50 truncate ml-2">{c.provenienza}</span></Link>)}
      {!d.ultimiContatti?.length && !d.richiamiOggi?.length && <p className="text-sm text-anthracite/45">Niente da gestire.</p>}
    </Panel>
  );
}

const FASI = [
  { n: 1, titolo: "Accessi, ruoli, permessi, registro, backup", stato: "completata" },
  { n: 2, titolo: "Contatti, immobili, richieste, abbinamenti e collegamento al sito", stato: "completata" },
  { n: 3, titolo: "Acquisizioni, incarichi, agenda, visite e documenti", stato: "completata" },
  { n: 4, titolo: "Trattative, provvigioni, dashboard e report", stato: "completata" },
  { n: 5, titolo: "Locazioni, importazioni, integrazioni e strumenti AI", stato: "da realizzare" },
];

function Fasi() {
  return (
    <Panel title="Avanzamento del gestionale" testid="g-dash-fasi">
      <ol className="space-y-3">
        {FASI.map((f) => (
          <li key={f.n} className="flex items-start gap-3">
            {f.stato === "completata" ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <CircleDashed className="h-5 w-5 text-anthracite/30 shrink-0" />}
            <div className="flex-1 text-sm"><b>Fase {f.n}</b> · {f.titolo}</div>
            <Badge tone={f.stato === "completata" ? "green" : "neutral"}>{f.stato}</Badge>
          </li>
        ))}
      </ol>
    </Panel>
  );
}

function UltimeOperazioni() {
  const [items, setItems] = useState(null);
  useEffect(() => { gapi.get("/audit", { params: { size: 6 } }).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  return (
    <Panel title="Ultime operazioni" actions={<Link to="/gestionale/registro" className="text-sm text-brand font-medium">Registro completo</Link>} testid="g-dash-audit">
      {!items ? <p className="text-sm text-anthracite/40">Caricamento…</p> : !items.length ? <p className="text-sm text-anthracite/40">Nessuna operazione registrata.</p> : (
        <ul className="divide-y divide-border-subtle">
          {items.map((a) => (
            <li key={a.id} className="py-2.5 flex justify-between gap-3 text-sm">
              <span><b>{a.userNome || a.userEmail || "Sistema"}</b> · {AUDIT_LABEL[a.azione] || a.azione}</span>
              <span className="text-anthracite/45 whitespace-nowrap">{fmtDateTime(a.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export default function Dashboard() {
  const { session, can } = useGAuth();
  const u = session.user;
  const [d, setD] = useState(null);
  useEffect(() => {
    Promise.all([gapi.get("/dashboard"), gapi.get("/scadenze")]).then(([a, b]) => setD({ ...a.data, incarichiInScadenza: b.data.incarichi?.length, documentiMancanti: b.data.documentiMancanti }))
      .catch(() => setD({}));
  }, []);
  return (
    <div data-testid="g-dashboard">
      <PageHeader eyebrow={new Intl.DateTimeFormat("it-IT", { timeZone: "Europe/Rome", weekday: "long", day: "numeric", month: "long" }).format(new Date())}
        title={`Buongiorno, ${u.nome.split(" ")[0]}`}
        desc="Dati in tempo reale, filtrati in base ai tuoi permessi. Agenda, trattative e provvigioni arrivano nelle prossime fasi." />
      {d && <Kpis d={d} />}
      <div className="grid lg:grid-cols-2 gap-5 mb-5">
        {can("attivita") && <TaskWidget />}
        {d && <Richiami d={d} />}
      </div>
      <div className="grid lg:grid-cols-3 gap-5">
        <Panel className="lg:col-span-1" testid="g-dash-account">
          <div className="flex items-center gap-3"><ShieldCheck className="h-9 w-9 text-brand" /><div><p className="font-semibold">{session.ruoli[u.ruolo]}</p><p className="text-xs text-anthracite/50">{u.email}</p></div></div>
          <dl className="mt-5 space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-anthracite/50">Ultimo accesso</dt><dd>{fmtDateTime(u.ultimoAccesso)}</dd></div>
            <div className="flex justify-between"><dt className="text-anthracite/50">Verifica in due passaggi</dt><dd>{u.ruolo === "titolare" || u.mfaEmail ? "Attiva (email)" : "Non attiva"}</dd></div>
            {u.ruolo === "titolare" && <div className="flex justify-between"><dt className="text-anthracite/50">Codici di recupero</dt><dd data-testid="g-dash-recovery-left">{session.codiciRecuperoRimasti} rimasti</dd></div>}
          </dl>
          <Link to="/gestionale/profilo" className="mt-5 inline-block text-sm text-brand font-medium">Gestisci sicurezza e sessioni</Link>
        </Panel>
        <div className="lg:col-span-2"><Fasi /></div>
        {can("registro") ? <div className="lg:col-span-3"><UltimeOperazioni /></div> : (
          <Panel className="lg:col-span-3"><p className="text-sm text-anthracite/50 flex items-center gap-2"><Lock className="h-4 w-4" /> I dati operativi saranno visibili in base ai permessi assegnati dal titolare.</p></Panel>
        )}
      </div>
    </div>
  );
}
